import { AxiosError, AxiosResponse } from 'axios';
import { IResponseErrorV2 } from 'types/IResponseApi';

/** Normalize an arbitrary error value into a safe, displayable string. */
const toErrorString = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const { message: nestedMessage } = value as { message?: unknown };
    if (typeof nestedMessage === 'string') return nestedMessage;
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value ?? '');
};

const asyncWrapper = async <T = any>(promise): Promise<[AxiosResponse<T>, string]> => {
  try {
    const res = await promise;
    return [res, undefined];
  } catch (e) {
    const { message: axiosMessage, response, request } = e as AxiosError;
    // Response can be absent for network/timeout errors — don't crash on it.
    let errRes = response?.data;

    if (
      request?.responseType === 'blob'
      && response?.data instanceof Blob
      && response.data.type
      && response.data.type.toLowerCase().indexOf('json') !== -1
    ) {
      errRes = JSON.parse(await response.data.text());
    }

    const { message, error }: Partial<IResponseErrorV2> = errRes ?? {};

    // Backend error bodies may carry message/error as arrays or objects
    // (e.g. NestJS validation errors) — always coerce to a string so the
    // caller can render it safely (e.g. in a snackbar).
    if (message) {
      const normalized = toErrorString(message);
      console.error(normalized);
      return [undefined, normalized];
    }

    if (error) {
      const normalized = toErrorString(error);
      console.error(normalized);
      return [undefined, normalized];
    }

    const normalized = toErrorString(axiosMessage);
    console.error(normalized);
    return [undefined, normalized];
  }
};

// eslint-disable-next-line import/prefer-default-export
export { asyncWrapper };
